import type { ReactNode } from "react";
import { classNames } from "primereact/utils";

import Vessel from "@/assets/icons/vessel.svg?react";
import { GanttBar, Icon } from "@/ds";
import type { GanttTone } from "@/ds";

import { RotuloObra } from "../rotulo-obra";
import { SIMULACAO_MARCA_CLASSES } from "../simulacao-marca";
import type { SegmentKind } from "../gantt-carga/gantt-rows";
import { GanttTip } from "../gantt-tip";
import type { AllocationBarItem, AllocationBarViewModel } from "./types-gantt-allocation-bar";
import styles from "./gantt-allocation-bar.module.scss";

/** Tom do `GanttBar` de cada etapa; a transferida é a obra com o contorno tracejado da tela. */
const SEGMENT_TONE: Record<SegmentKind, GanttTone> = {
  planejamento: "info",
  obra: "primary",
  fechamento: "success",
  cancelada: "danger",
  transferida: "primary",
};

function segmentLabel(
  kind: SegmentKind,
  item: AllocationBarItem,
  vessel: boolean,
  everySegment: boolean,
): ReactNode {
  const texto = item.rotulo ? <RotuloObra {...item.rotulo} /> : item.label;
  if (kind === "transferida") {
    return (
      <>
        <Icon name="star" size={12} />
        {texto}
      </>
    );
  }
  if (kind === "obra" || kind === "cancelada" || everySegment) {
    return (
      <>
        {vessel ? <Vessel aria-hidden /> : null}
        {texto}
      </>
    );
  }
  return null;
}

/**
 * Barra de uma alocação sobre o `GanttBar` do DS, com o card do tooltip e a marca da simulação. Com uma etapa só
 * (Obra, Cancelada, Transferida) a própria barra leva o rótulo; com várias, a barra é a pílula (recorta as pontas) e
 * cada etapa é um `GanttBar` em faixa, posicionado em % da pílula. A indisponibilidade é a pílula do relógio.
 */
export function AllocationBarView({
  item,
  layout,
  comMarca,
  marcaPreenchida,
  vessel,
  plainTitle,
  somenteIcone,
  rotuloExterno,
  common,
  tip,
}: AllocationBarViewModel) {
  if (item.kind === "indisponibilidade") {
    // A pílula estica pelos dias do período (QA); estreita demais, fica só o ícone e o "Nd" sai ao lado dela.
    return (
      <>
        <GanttBar
          {...common}
          tone="neutral"
          className={styles.indisponibilidade}
          classNames={{ label: styles.label }}
          label={
            <>
              <Icon name="clock" size={14} />
              {somenteIcone ? null : ` ${item.label}`}
            </>
          }
        />
        {rotuloExterno ? (
          <span
            className={styles.rotuloExterno}
            data-lado={rotuloExterno.lado}
            data-past={item.past ? true : undefined}
            style={rotuloExterno.style}
            aria-hidden
          >
            {rotuloExterno.texto}
          </span>
        ) : null}
        {plainTitle ? null : <GanttTip tip={tip} />}
      </>
    );
  }

  const [first] = item.segments;
  const single = item.segments.length === 1 && first;
  const marcaClasses = comMarca
    ? classNames(SIMULACAO_MARCA_CLASSES.barra, marcaPreenchida && SIMULACAO_MARCA_CLASSES.pilula)
    : undefined;
  const className = classNames(
    single && styles.unica,
    single && single.kind === "transferida" && styles.transferida,
    marcaClasses,
  );

  return (
    <>
      <GanttBar
        {...common}
        tone={single ? SEGMENT_TONE[single.kind] : "primary"}
        className={className}
        data-simulado={comMarca ? true : undefined}
        classNames={{ label: single ? styles.label : styles.segments }}
        label={
          single ? (
            segmentLabel(single.kind, item, vessel, plainTitle)
          ) : (
            <>
              {item.segments.map((segment) => (
                <GanttBar
                  key={segment.kind}
                  start={segment.left}
                  end={segment.left + segment.width}
                  tone={SEGMENT_TONE[segment.kind]}
                  variant="segment"
                  layout={layout}
                  past={item.past}
                  className={styles.segment}
                  classNames={{ label: styles.label }}
                  style={{ top: 0, height: "100%" }}
                  label={segmentLabel(segment.kind, item, vessel, false)}
                />
              ))}
            </>
          )
        }
      />
      {plainTitle ? null : <GanttTip tip={tip} />}
    </>
  );
}
